/* One cart handoff for every quick-add surface. The add request belongs to its
 * original component; this controller only manages the pending UI and rendering. */
(() => {
  'use strict';
  if (window.PiccoCartFeedback) return;
  const pending = new Map();
  const labels = window.PiccoCartFeedbackLabels || {};
  let sequence = 0;
  let renderQueue = Promise.resolve();
  const rootRoute = () => window.Shopify?.routes?.root || '/';
  const drawer = () => document.getElementById('CartDrawer');
  const node = (tag, className, text) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text) el.textContent = text;
    return el;
  };

  function mount() {
    const cart = drawer();
    if (!cart) return;
    let host = cart.querySelector('[data-picco-cart-feedback]');
    if (!host) {
      host = node('ul', 'picco-cart-feedback');
      host.dataset.piccoCartFeedback = '';
      const scrollable = cart.querySelector('.drawer__scrollable');
      if (scrollable) scrollable.prepend(host);
      else cart.querySelector('.drawer__header')?.insertAdjacentElement('afterend', host);
    }
    if (!host.isConnected) return;
    const busy = [...pending.values()].some(task => task.state === 'pending');
    cart.toggleAttribute('data-picco-pending', busy);
    host.setAttribute('aria-busy', String(busy));
    const rows = [...pending.values()].filter(task => !task.settled).reverse().map(task => task.row);
    const list = cart.querySelector('.picco-cart-items__list');
    if (list) {
      // Use the final line's position and dimensions from the first frame.
      list.prepend(...rows);
      host.hidden = true;
    } else {
      host.replaceChildren(...rows);
      host.hidden = !rows.length;
    }
    // Capture checkout while an add is unresolved, without altering the theme's
    // own sold-out/disabled states. Pending items never count as confirmed items.
    cart.querySelectorAll('[name="checkout"], .picco-checkout').forEach(button => {
      if (busy) button.setAttribute('data-picco-checkout-pending', '');
      else button.removeAttribute('data-picco-checkout-pending');
    });
  }

  function makeRow(product, variant) {
    const row = node('li', 'picco-cart-pending');
    const media = node('div', 'picco-cart-pending__image');
    const image = variant?.featured_image || product?.featured_image || product?.image || product?.images?.[0] || product?.gallery?.[0];
    const src = typeof image === 'string' ? image : image?.src || image?.url;
    if (src) {
      const img = node('img');
      img.src = src;
      img.alt = '';
      media.append(img);
    }
    const copy = node('div', 'picco-cart-pending__copy');
    const title = node('div', 'picco-cart-pending__title', product?.title || product?.cardTitle);
    const option = node('div', 'picco-cart-pending__option', variant?.title === 'Default Title' ? '' : variant?.title);
    const skeleton = node('div', 'picco-cart-pending__skeleton');
    skeleton.setAttribute('aria-hidden', 'true');
    const status = node('div', 'picco-cart-pending__status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    const spinner = node('span', 'picco-cart-pending__spinner');
    spinner.setAttribute('aria-hidden', 'true');
    media.append(spinner);
    status.append(node('span', '', labels.adding));
    copy.append(title, option, skeleton, status);
    row.append(media, copy);
    return row;
  }

  async function showCart(trigger) {
    const cart = drawer();
    if (!cart) return;
    if (!cart.hasAttribute('open') && !cart.hasAttribute('active')) {
      if (typeof cart.show === 'function') await cart.show(trigger);
      else if (typeof cart.open === 'function') await cart.open(trigger);
      else document.querySelector('[aria-controls="CartDrawer"]:not(#CartDrawer *)')?.click();
    }
    // A quick-add closer may restore focus after its animation, even when the
    // cart was already open underneath it. Restore the cart's own focus trap.
    window.theme?.a11y?.trapFocus?.(cart, cart.querySelector('.drawer__close'));
  }

  function begin({ shell, product = {}, variant = {}, trigger, close, duration = 500 }) {
    if (!drawer()) return null;
    const variantId = String(variant?.id || '');
    const wasExisting = [...drawer().querySelectorAll('[data-picco-cart-item]')].some(item => item.dataset.variantId === variantId);
    const task = { id: ++sequence, state: 'pending', row: makeRow(product, variant), trigger, variantId, wasExisting };
    for (const other of pending.values()) {
      if (other.state === 'pending') { other.overlapping = true; task.overlapping = true; }
    }
    pending.set(task.id, task);
    mount();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const wait = reduced ? 0 : duration;
    const panel = shell?.querySelector('.pc-quick-add__panel, .picco-rf__drawer-sheet, .picco-iwp__quick-panel, .comp-portal-inner, .quick-view__content, .pcu-dialog__panel') || shell;
    const oldTransform = panel?.style.getPropertyValue('transform') || '';
    const oldPriority = panel?.style.getPropertyPriority('transform') || '';
    const oldTransition = panel?.style.getPropertyValue('transition') || '';
    const oldTransitionPriority = panel?.style.getPropertyPriority('transition') || '';
    const isDialog = shell?.tagName === 'DIALOG';
    if (shell) shell.dataset.piccoCartHandoff = 'true';
    if (panel && !reduced) {
      // Force a downward exit on desktop as well as mobile.
      panel.style.setProperty('transition', `transform ${duration}ms cubic-bezier(.4,0,.2,1)`, 'important');
      panel.getBoundingClientRect();
    }
    if (!isDialog) close?.();
    if (panel && !reduced) panel.style.setProperty('transform', 'translateY(110%)', 'important');
    task.ready = new Promise(resolve => window.setTimeout(async () => {
      if (isDialog) close?.();
      if (panel) {
        if (oldTransform) panel.style.setProperty('transform', oldTransform, oldPriority);
        else panel.style.removeProperty('transform');
        if (oldTransition) panel.style.setProperty('transition', oldTransition, oldTransitionPriority);
        else panel.style.removeProperty('transition');
      }
      if (shell) delete shell.dataset.piccoCartHandoff;
      mount();
      try { await showCart(trigger); } catch (error) { console.warn('[Picco cart] Drawer could not open', error); }
      resolve();
    }, wait ? wait + 30 : 0));
    return task;
  }

  function sectionIds() {
    const ids = [drawer()?.dataset.sectionId, document.querySelector('header[data-section-id]')?.dataset.sectionId];
    return [...new Set(ids.filter(Boolean))];
  }

  async function readSections() {
    const url = new URL(location.href);
    url.searchParams.set('sections', sectionIds().join(','));
    const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(labels.refreshError);
    const result = await response.json();
    return result.sections || result;
  }

  function applySections(sections) {
    const cart = drawer();
    const html = sections?.[cart?.dataset.sectionId];
    if (typeof html !== 'string' || !new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer .drawer__content')) {
      throw new Error(labels.refreshError);
    }
    if (typeof window.piccoApplyRenderedCartSections === 'function') {
      window.piccoApplyRenderedCartSections(sections, false);
    } else {
      const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
      const content = cart.querySelector('.drawer__content');
      if (content) content.innerHTML = incoming.querySelector('.drawer__content').innerHTML;
      const count = cart.querySelector('.picco-cart-count');
      if (count && incoming.querySelector('.picco-cart-count')) count.textContent = incoming.querySelector('.picco-cart-count').textContent;
    }
  }

  function isPendingVariant(variantId) {
    return [...pending.values()].some(task => task.state === 'pending' && task.variantId === String(variantId));
  }

  function settleRow(task, result) {
    if (task.settled) return;
    const key = result?.key || result?.items?.[0]?.key;
    const items = [...drawer().querySelectorAll('[data-picco-cart-item]')];
    const confirmed = items.find(item => key && item.dataset.key === key)
      || items.find(item => task.variantId && item.dataset.variantId === task.variantId);
    if (!confirmed) return;
    task.settled = true;
    if (task.wasExisting || confirmed.dataset.piccoCartResolved === 'true') { task.row.remove(); return; }

    // Promote the original placeholder node and its already-loaded image into
    // the confirmed line. No second image, entry animation or fade to blank.
    const row = task.row;
    const image = row.querySelector('.picco-cart-pending__image img');
    const media = confirmed.querySelector('.picco-cart-media');
    const children = [...confirmed.children].map(child => {
      if (child !== media || !image) return child.cloneNode(true);
      const link = child.cloneNode(false);
      image.classList.add('picco-cart-image-resolving');
      image.dataset.piccoPreservedImage = 'true';
      link.append(image);
      return link;
    });
    [...row.attributes].forEach(attribute => row.removeAttribute(attribute.name));
    [...confirmed.attributes].forEach(attribute => row.setAttribute(attribute.name, attribute.value));
    row.classList.remove('is-entering');
    row.dataset.piccoCartResolved = 'true';
    row.replaceChildren(...children);
    confirmed.replaceWith(row);
    if (image) {
      image.getBoundingClientRect();
      requestAnimationFrame(() => image.classList.add('is-clear'));
      window.setTimeout(() => image.classList.remove('picco-cart-image-resolving', 'is-clear'), 400);
    }
  }

  async function syncCounts() {
    try {
      const response = await fetch(rootRoute() + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('cart-count').forEach(count => {
        count.textContent = String(cart.item_count);
        count.hidden = cart.item_count === 0;
      });
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart, source: 'picco-cart-feedback' } }));
    } catch (_) { /* The server-rendered cart is already correct. Never retry an add. */ }
  }

  async function complete(task, result) {
    if (!task) return;
    await task.ready;
    const render = async () => {
      try {
        // If requests overlap, render current server state instead of an older
        // bundled response that could remove a more recently added item.
        const sections = task.overlapping || !result?.sections?.[drawer()?.dataset.sectionId]
          ? await readSections() : result.sections;
        applySections(sections);
        settleRow(task, result);
        for (const other of pending.values()) {
          if (other !== task && other.state === 'pending' && !other.wasExisting) settleRow(other);
        }
        pending.delete(task.id);
        if (!task.settled) task.row.remove();
        mount();
        await syncCounts();
      } catch (error) {
        await fail(task, labels.refreshError, true);
      }
    };
    renderQueue = renderQueue.then(render, render);
    await renderQueue;
  }

  async function fail(task, error, accepted = false) {
    if (!task) return;
    task.state = 'error';
    await task.ready;
    task.row.classList.add('is-error');
    task.row.querySelector('.picco-cart-pending__skeleton')?.remove();
    task.row.querySelector('.picco-cart-pending__spinner')?.remove();
    const status = task.row.querySelector('.picco-cart-pending__status');
    status.replaceChildren();
    status.setAttribute('role', 'alert');
    status.append(node('span', '', accepted ? labels.refreshError : (error instanceof TypeError ? labels.connectionError : error?.message || error || labels.error)));
    const link = node('a', 'picco-cart-pending__cart-link', labels.viewCart);
    link.href = rootRoute() + 'cart';
    const dismiss = node('button', 'picco-cart-pending__dismiss', labels.close);
    dismiss.type = 'button';
    dismiss.addEventListener('click', () => { pending.delete(task.id); task.row.remove(); mount(); });
    status.append(link, dismiss);
    mount();
  }

  document.addEventListener('click', event => {
    if (event.target.closest('[data-picco-checkout-pending]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('submit', event => {
    if (pending.size && event.submitter?.closest('[data-picco-checkout-pending]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  window.PiccoCartFeedback = { begin, complete, fail, isPendingVariant };
})();
